'use client';

import { FC, useCallback, useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { GetActiveGateGroupQuery } from '../../../../../generated/graphql';
import { UseActiveGateGroupFilter, useActiveGateGroup } from '../../../../hooks/use-active-gate-group';
import { useTranslations } from '../../../../hooks/use-translations';

import { SelectForm, SelectFormOption, SelectFormProps } from './SelectForm';

type GateGroupSelectFormProps = Omit<SelectFormProps<string>, GateGroupSelectFromOmit> & {
  watchDeps?: Partial<GateGroupSelectFormWatchDeps>;
  withEmpty?: boolean;
  availableGate?: boolean;
};

type GateGroupSelectFromOmit = 'options' | 'onChange' | 'onBlur' | 'ref' | 'value';

type GateGroupSelectFormWatchDeps = {
  sourceMerchantField: string;
  sourceProviderField: string;
  sourceCurrencyField: string;
  sourceGateTypeField: string;
};

export const GateGroupSelectForm: FC<GateGroupSelectFormProps> = (props) => {
  const { name, watchDeps, withEmpty, placeholder, ...otherProps } = props;

  const SOURCE_GATE_ID_STATE = 'gate_id';
  const SOURCE_GATE_GROUP_ID_STATE = 'gate_group_id';
  const SOURCE_GATE_CURRENCY_ID_STATE = 'gate_currency_id';

  const tMisc = useTranslations('misc');
  const t = useTranslations('dictionaries.gate');

  const { watch, resetField, setValue } = useFormContext();
  const {
    field: controllerField,
    formState: { errors },
  } = useController({ name: SOURCE_GATE_GROUP_ID_STATE });

  const gate_group_id = watch('gate_group_id');

  const getFilter = (deps: Partial<GateGroupSelectFormWatchDeps>) => {
    const { sourceMerchantField, sourceProviderField, sourceCurrencyField, sourceGateTypeField } = deps;

    return {
      merchant_id: sourceMerchantField ? watch(sourceMerchantField) : undefined,
      provider_id: sourceProviderField ? watch(sourceProviderField) : undefined,
      currency_id: sourceCurrencyField ? watch(sourceCurrencyField) : undefined,
      gate_type_id: sourceGateTypeField ? watch(sourceGateTypeField) : undefined,
    } as UseActiveGateGroupFilter;
  };

  const exactFilter = watchDeps ? getFilter(watchDeps) : undefined;
  const { data, loading } = useActiveGateGroup({ filter: exactFilter });

  const generateSelectOptions = (data: GetActiveGateGroupQuery) => {
    const options: SelectFormOption<string>[] = [];

    if (withEmpty) {
      options.push({ value: '', name: t('all') });
    }

    const isGateType = Boolean(exactFilter?.gate_type_id);

    const addOption = (
      gate_group: GetActiveGateGroupQuery['gate_group'][0],
      active_gate?: GetActiveGateGroupQuery['gate_group'][0]['active_gate'][0]
    ) => {
      options.push({
        // value: active_gate ? active_gate.id || '' : gate_group.id || '',
        value: gate_group.id || '',
        name: gate_group.name || '',
      });
    };

    data.gate_group.forEach((current_gate_group) => {
      isGateType
        ? current_gate_group.active_gate.forEach((current_active_gate) => {
            addOption(current_gate_group, current_active_gate);
          })
        : addOption(current_gate_group);
    });

    return options;
  };

  /* It is not recommended to influence dependencies that represent fully registered form fields, as this may lead to violation of architectural boundaries. Use the form state that belongs to the field. */
  const updateDepsFormStateCurrentField = useCallback(
    (gate_group_id: string) => {
      const targetGateGroupData = data?.gate_group.find((currentGateGroup) => currentGateGroup.id === gate_group_id);

      const currentActiveGateData = targetGateGroupData?.active_gate[0];

      setValue(SOURCE_GATE_ID_STATE, currentActiveGateData?.id);
      setValue(SOURCE_GATE_CURRENCY_ID_STATE, currentActiveGateData?.currency_id);
    },
    [data?.gate_group, setValue]
  );

  useEffect(() => {
    const { unsubscribe } = watch((value, info) => {
      if (info.name !== SOURCE_GATE_GROUP_ID_STATE) {
        return;
      }

      updateDepsFormStateCurrentField(value[SOURCE_GATE_GROUP_ID_STATE]);
    });

    return () => unsubscribe();
  }, [SOURCE_GATE_GROUP_ID_STATE, updateDepsFormStateCurrentField, watch]);

  useEffect(() => {
    const sourceCurrencyField = watchDeps?.sourceCurrencyField;

    if (!sourceCurrencyField) {
      return;
    }

    const { unsubscribe } = watch((value, info) => {
      if (info.name !== sourceCurrencyField) {
        return;
      }

      const currency_id = value[sourceCurrencyField];
      const gate_currency_id = value[SOURCE_GATE_CURRENCY_ID_STATE];

      if (currency_id === gate_currency_id) {
        return;
      }

      setValue(SOURCE_GATE_ID_STATE, null);
      setValue(SOURCE_GATE_GROUP_ID_STATE, null);
      setValue(SOURCE_GATE_CURRENCY_ID_STATE, null);
    });

    return () => unsubscribe();
  }, [watch, resetField, controllerField.name, watchDeps?.sourceCurrencyField, setValue]);

  const prepareFieldValue = useMemo(() => {
    if (!data?.gate_group.length) {
      return '';
    }

    return gate_group_id ?? '';
  }, [data?.gate_group.length, gate_group_id]);

  return (
    <SelectForm
      {...controllerField}
      value={prepareFieldValue}
      error={errors && errors[controllerField.name] != null}
      helperText={errors[controllerField.name]?.message?.toString()}
      placeholder={loading ? tMisc('loading') : placeholder}
      options={data ? generateSelectOptions(data) : []}
      {...otherProps}
    />
  );
};
